import { AdvEmpty, AdvHeading, AdvListing, AdvSection, Btn, DirectoryFilters, WideCard, useText } from '@kkf/design'
import { TOURS, TOURS_HEADING, TOURS_PURPOSE } from '../data/tours'
import { useListingFilter } from '../components/useListingFilter'
import { useVocabulary } from '../hooks/useVocabulary'
import { useT } from '../i18n'

/**
 * Tours & Events — the bookable listing, searchable and filtered by kind.
 *
 * ── What changed, and why the grouping went ───────────────────────────────────────────
 *
 * This page used to open with a `Breadcrumb` photograph and then stack three `Band`
 * sections, one per kind, on alternating pale grounds. Both are gone.
 *
 * The banner went because on this register a photograph with a title over it is the
 * *hero's* device, and there is one hero per site. A listing opens with a heading on the
 * page's own ground instead, with the fixed bar's height reserved above it.
 *
 * The grouping went because a filter does the same job better. Three fixed sections tell a
 * visitor what the three kinds are and make them scroll past two of them; a rail lets them
 * say which one they want and shows only that. The kinds survive as the filter's options,
 * so nothing was lost — and a search box now also catches "two day" and "beginner", which
 * no amount of grouping would have.
 */
export function ToursPage() {
  const t = useT()
  const word = useVocabulary()
  const s = useText()
  const f = useListingFilter({
    items: TOURS,
    groupId: 'kind',
    groupTitle: t('tours.group'),
    groupOf: (tour) => tour.kind,
    labelOf: word,
    // Everything a visitor might plausibly type. The specs are in here because "two day"
    // and "May to October" are in the spec details, not in the name or the tagline.
    // Both languages — see `ServicesPage`. A spec's `detail` is `Text | string`, because
    // some are figures and some are sentences.
    searchText: (tour) =>
      [
        tour.name.en,
        tour.name.mn,
        tour.tagline.en,
        tour.tagline.mn,
        tour.kind,
        ...tour.specs.flatMap((spec) => [
          spec.term.en,
          spec.term.mn,
          typeof spec.detail === 'string' ? spec.detail : `${spec.detail.en} ${spec.detail.mn}`,
        ]),
      ].join(' '),
    order: ['Tour', 'Training', 'Rental'],
  })

  return (
    <>
      <AdvSection glow="tr" className="ul-adv-page-top" containerClassName="wow animate__fadeInUp">
        {/* This page's own name — the one <h1> on it. The garage's inner pages carry no
            breadcrumb banner, so the opening heading is the page heading. */}
        <AdvHeading
          eyebrow={s(TOURS_HEADING.subTitle)}
          title={t('home.trail')}
          titleAccent={t('home.trailAccent')}
          lead={s(TOURS_HEADING.descr)}
          as="h1"
        />
      </AdvSection>

      <AdvSection glow="bl" flush containerClassName="wow animate__fadeInUp">
        <AdvListing
          rail={
            <DirectoryFilters
              search={f.search}
              onSearchChange={f.setSearch}
              searchPlaceholder={t('tours.search')}
              groups={f.groups}
              selected={f.selected}
              onToggle={f.toggle}
              onClear={f.clear}
              resultSummary={f.summary}
            />
          }
        >
          {f.results.length === 0 ? (
            <AdvEmpty onClear={f.clear}>
              No tour, training day or rental matches that. The full list is six items —
              clearing the filters shows all of them.
            </AdvEmpty>
          ) : (
            <div className="row row-cols-xl-2 row-cols-1 ul-bs-row">
              {f.results.map((tour) => (
                <div className="col" key={tour.slug}>
                  <WideCard
                    item={{
                      meta: tour.kind,
                      title: s(tour.name),
                      descr: s(tour.tagline),
                      image: tour.image,
                      imageAlt: s(tour.imageAlt),
                      linkLabel: 'View the tour',
                      to: `/tours/${tour.slug}`,
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </AdvListing>
      </AdvSection>

      {/* WHY WE RUN THEM — the trail is not the whole point. */}
      <AdvSection glow="band" containerClassName="wow animate__fadeInUp">
        <AdvHeading
          centered
          eyebrow={t('tours.whyEyebrow')}
          title={t('tours.dayOut')}
          titleAccent={t('home.loveAccent')}
          lead={s(TOURS_PURPOSE.lead)}
          aside={undefined}
        />
        <div className="text-center">
          <Btn to="/programmes" className="ul-btn--ghost">
            What your booking pays for
          </Btn>
        </div>
      </AdvSection>
    </>
  )
}
